import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import {
  Info as InfoIcon,
  DeleteOutline as DeleteIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { Tooltip, Checkbox, FormControlLabel, Chip, Autocomplete, TextField } from '@mui/material';
import CustomButton from '../../../components/common/Button';
import PreAssignedDriversSelector from '../components/PreAssignedDriversSelector';
import { ShiftTemplateFormData, RecurrenceType, TemplateStatus } from '../types';
import { CREATE_SHIFT_TEMPLATE, UPDATE_SHIFT_TEMPLATE, FETCH_SHIFT_TEMPLATE, ASSIGN_COURIER_TO_TEMPLATE, UNASSIGN_COURIER_FROM_TEMPLATE } from '../../../api/mutations/ShiftManagementMutations';
import { stagingOptions } from '../../../constants/apollo';
import { cityOptions } from '../../../constants/enums';
import DatePickerDDMMYY from '../../../components/common/DatePickerDDMMYY';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const CreateShiftTemplate: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = !!id;

  const { data: templateData, loading: templateLoading, error: templateError } = useQuery(FETCH_SHIFT_TEMPLATE, {
    ...stagingOptions,
    variables: { id: id || '' },
    skip: !id,
    fetchPolicy: 'network-only',
  });

  const existingTemplate = useMemo(() => {
    if (!templateData?.fetchShiftTemplate) return undefined;
    const apiTemplate = templateData.fetchShiftTemplate;
    return {
      id: apiTemplate.id,
      name: apiTemplate.name,
      startTime: apiTemplate.startTime,
      endTime: apiTemplate.endTime,
      requiredDrivers: apiTemplate.requiredDrivers || 1,
      cityId: apiTemplate.city?.id || undefined,
      neighborhoodId: apiTemplate.neighborhood?.id || undefined,
      recurrenceType: (apiTemplate.recurrenceType?.toLowerCase() || 'daily') as RecurrenceType,
      daysOfWeek: apiTemplate.daysOfWeek || [],
      status: (apiTemplate.status?.toLowerCase() || 'active') as TemplateStatus,
      generateDaysAhead: apiTemplate.generateDaysAhead || 0,
      validFrom: apiTemplate.validFrom || '',
      validUntil: apiTemplate.validUntil || '',
      specificDate: apiTemplate.specificDate || '',
      excludeDates: apiTemplate.excludeDates || [],
      priority: apiTemplate.priority,
      courierIds: apiTemplate.couriers?.map((c: any) => c.id) || [],
    };
  }, [templateData]);

  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [createShiftTemplateMutation] = useMutation(CREATE_SHIFT_TEMPLATE, {
    ...stagingOptions,
    fetchPolicy: 'no-cache',
  });
  const [updateShiftTemplateMutation] = useMutation(UPDATE_SHIFT_TEMPLATE, {
    ...stagingOptions,
    fetchPolicy: 'no-cache',
  });
  const [assignCourierToTemplateMutation] = useMutation(ASSIGN_COURIER_TO_TEMPLATE, {
    ...stagingOptions,
    fetchPolicy: 'no-cache',
  });
  const [unassignCourierFromTemplateMutation] = useMutation(UNASSIGN_COURIER_FROM_TEMPLATE, {
    ...stagingOptions,
    fetchPolicy: 'no-cache',
  });

  const [formData, setFormData] = useState<ShiftTemplateFormData>({
    name: '',
    startTime: '',
    endTime: '',
    requiredDrivers: 1,
    cityId: undefined,
    neighborhoodId: undefined,
    recurrenceType: 'daily',
    status: 'active',
    generateDaysAhead: 0,
    daysOfWeek: [],
    validFrom: '',
    validUntil: '',
    specificDate: '',
    excludeDates: [],
    priority: undefined,
    courierIds: [],
  });


  useEffect(() => {
    if (isEditMode && !templateLoading) {
      if (templateError) {
        console.error('Error fetching template:', templateError);
        toast.error('Failed to load shift schedule. Please try again.');
        navigate('/shift-management');
        return;
      }

      if (existingTemplate) {
        setFormData({
          name: existingTemplate.name,
          startTime: existingTemplate.startTime,
          endTime: existingTemplate.endTime,
          requiredDrivers: existingTemplate.requiredDrivers,
          cityId: existingTemplate.cityId,
          neighborhoodId: existingTemplate.neighborhoodId,
          recurrenceType: existingTemplate.recurrenceType,
          status: existingTemplate.status,
          generateDaysAhead: existingTemplate.generateDaysAhead || 0,
          daysOfWeek: existingTemplate.daysOfWeek || [],
          validFrom: existingTemplate.validFrom || '',
          validUntil: existingTemplate.validUntil || '',
          specificDate: existingTemplate.specificDate || '',
          excludeDates: existingTemplate.excludeDates || [],
          priority: existingTemplate.priority,
          courierIds: existingTemplate.courierIds || [],
          startLatitude: undefined,
          startLongitude: undefined,
        });
      } else if (templateData && !templateData.fetchShiftTemplate) {
        toast.error('Shift schedule not found');
        navigate('/shift-management');
      }
    }
  }, [isEditMode, existingTemplate, templateLoading, templateError, templateData, navigate]);

  useEffect(() => {
    if (formData.recurrenceType === 'specific_date' &&
      formData.specificDate &&
      typeof formData.specificDate === 'string' &&
      formData.specificDate.trim() !== '' &&
      formData.specificDate.length >= 8) {
      setFieldErrors((prev) => {
        if (prev.specificDate) {
          const newErrors = { ...prev };
          delete newErrors.specificDate;
          return newErrors;
        }
        return prev;
      });
    }
  }, [formData.specificDate, formData.recurrenceType]);

  const handleInputChange = (field: keyof ShiftTemplateFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    if (field === 'specificDate') {
      setFieldErrors((prev) => {
        if (prev.specificDate) {
          const newErrors = { ...prev };
          delete newErrors.specificDate;
          return newErrors;
        }
        return prev;
      });
    }
  };

  const handleDayToggle = (dayValue: number) => {
    setFormData((prev) => {
      const currentDays = prev.daysOfWeek || [];
      const newDays = currentDays.includes(dayValue)
        ? currentDays.filter(d => d !== dayValue)
        : [...currentDays, dayValue].sort();
      return { ...prev, daysOfWeek: newDays };
    });
  };

  const [newExcludeDate, setNewExcludeDate] = useState('');

  const handleAddExcludeDate = () => {
    if (!newExcludeDate) return;
    if (formData.excludeDates?.includes(newExcludeDate)) {
      alert('This date is already excluded');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      excludeDates: [...(prev.excludeDates || []), newExcludeDate].sort(),
    }));
    setNewExcludeDate('');
  };

  const handleRemoveExcludeDate = (date: string) => {
    setFormData((prev) => ({
      ...prev,
      excludeDates: prev.excludeDates?.filter(d => d !== date) || [],
    }));
  };

  const handleSave = async () => {
    const errors: Record<string, string> = {};

    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'Shift Schedule Name is required';
    } else if (formData.name.trim().length < 3) {
      errors.name = 'Shift Schedule Name must be at least 3 characters';
    } else if (formData.name.trim().length > 255) {
      errors.name = 'Shift Schedule Name must not exceed 255 characters';
    }

    if (!formData.startTime) {
      errors.startTime = 'Start Time is required';
    }

    if (!formData.endTime) {
      errors.endTime = 'End Time is required';
    }

    if (formData.startTime && formData.endTime) {
      const [startHours, startMinutes] = formData.startTime.split(':').map(Number);
      const [endHours, endMinutes] = formData.endTime.split(':').map(Number);

      if (isNaN(startHours) || isNaN(startMinutes)) {
        errors.startTime = 'Invalid Start Time format';
      }

      if (isNaN(endHours) || isNaN(endMinutes)) {
        errors.endTime = 'Invalid End Time format';
      } else if (!errors.startTime) {
        const startTotal = startHours * 60 + startMinutes;
        const endTotal = endHours * 60 + endMinutes;
        if (endTotal <= startTotal) {
          errors.endTime = 'End time must be after start time';
        }
      }
    }

    const requiredDrivers = formData.requiredDrivers || 1;
    if (requiredDrivers < 1) {
      errors.requiredDrivers = 'Required Drivers must be at least 1';
    } else if (requiredDrivers > 100000) {
      errors.requiredDrivers = 'Required Drivers must not exceed 100,000 (1 lakh)';
    }

    if (formData.recurrenceType === 'weekly' && (!formData.daysOfWeek || formData.daysOfWeek.length === 0)) {
      errors.daysOfWeek = 'Please select at least one day of the week for weekly recurrence';
    }

    if (formData.recurrenceType === 'specific_date') {
      const specificDate = formData.specificDate;
      const hasValidDate = specificDate &&
        typeof specificDate === 'string' &&
        specificDate.trim() !== '' &&
        specificDate.length >= 8;

      if (!hasValidDate) {
        errors.specificDate = 'Specific date is required for specific date recurrence type';
      }
    }

    if (formData.recurrenceType === 'custom_range') {
      if (!formData.validFrom) {
        errors.validFrom = 'Valid From date is required for custom range';
      }
      if (!formData.validUntil) {
        errors.validUntil = 'Valid Until date is required for custom range';
      }
      if (formData.validFrom && formData.validUntil) {
        const fromDate = new Date(formData.validFrom);
        const untilDate = new Date(formData.validUntil);
        if (untilDate <= fromDate) {
          errors.validUntil = 'Valid Until date must be after Valid From date';
        }
      }
    }

    if (!formData.cityId) {
      errors.cityId = 'City is required';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstError = Object.values(errors)[0];
      toast.error(firstError);
      return;
    }

    setFieldErrors({});

    try {
      setSaving(true);

      if (isEditMode && id) {
        const updateInput: any = {
          id: id,
        };

        if (formData.name && formData.name.trim() !== '') updateInput.name = formData.name.trim();
        if (formData.requiredDrivers !== undefined) updateInput.requiredDrivers = formData.requiredDrivers;
        if (formData.status) updateInput.status = formData.status.toUpperCase();
        if (formData.startTime) updateInput.startTime = formData.startTime;
        if (formData.endTime) updateInput.endTime = formData.endTime;
        if (formData.cityId) updateInput.cityId = String(formData.cityId);
        if (formData.neighborhoodId) updateInput.neighborhoodId = formData.neighborhoodId;
        if (formData.recurrenceType) updateInput.recurrenceType = formData.recurrenceType.toUpperCase();
        if (formData.daysOfWeek && formData.daysOfWeek.length > 0) updateInput.daysOfWeek = formData.daysOfWeek;
        if (formData.specificDate) updateInput.specificDate = formData.specificDate;
        if (formData.validFrom) updateInput.validFrom = formData.validFrom;
        if (formData.validUntil) updateInput.validUntil = formData.validUntil;
        if (formData.excludeDates && formData.excludeDates.length > 0) updateInput.excludeDates = formData.excludeDates;
        if (formData.priority !== undefined) updateInput.priority = formData.priority;

        const response = await updateShiftTemplateMutation({
          variables: { input: updateInput },
        });

        if (response.data?.updateShiftTemplate?.shiftTemplate) {
          const oldCourierIds = existingTemplate?.courierIds || [];
          const newCourierIds = formData.courierIds || [];

          const toUnassign = oldCourierIds.filter((id: string) => !newCourierIds.includes(id));
          const toAssign = newCourierIds.filter((id: string) => !oldCourierIds.includes(id));

          for (const courierId of toUnassign) {
            try {
              await unassignCourierFromTemplateMutation({
                variables: {
                  input: {
                    shiftTemplateId: id,
                    courierId: courierId,
                  },
                },
              });
            } catch (error) {
              console.error(`Failed to unassign courier ${courierId}:`, error);
            }
          }

          for (const courierId of toAssign) {
            try {
              await assignCourierToTemplateMutation({
                variables: {
                  input: {
                    shiftTemplateId: id,
                    courierId: courierId,
                  },
                },
              });
            } catch (error) {
              console.error(`Failed to assign courier ${courierId}:`, error);
            }
          }

          toast.success('Shift schedule updated successfully');
          navigate('/shift-management', { state: { refresh: true, tab: 'templates' } });
        } else {
          console.error('Unexpected response structure:', response.data);
          toast.error('Shift schedule update failed. Please try again.');
        }
      } else {
        const createInput: any = {
          name: formData.name,
          startTime: formData.startTime,
          endTime: formData.endTime,
          requiredDrivers: formData.requiredDrivers || 1,
          recurrenceType: formData.recurrenceType.toUpperCase(),
          cityId: String(formData.cityId),
        };
        if (formData.neighborhoodId) createInput.neighborhoodId = String(formData.neighborhoodId);
        if (formData.specificDate) createInput.specificDate = formData.specificDate;
        if (formData.daysOfWeek && formData.daysOfWeek.length > 0) createInput.daysOfWeek = formData.daysOfWeek;
        if (formData.validFrom) createInput.validFrom = formData.validFrom;
        if (formData.validUntil) createInput.validUntil = formData.validUntil;
        if (formData.excludeDates && formData.excludeDates.length > 0) createInput.excludeDates = formData.excludeDates;
        if (formData.priority !== undefined) createInput.priority = formData.priority;

        const response = await createShiftTemplateMutation({
          variables: { input: createInput },
        });

        if (response.errors && response.errors.length > 0) {
          const errorMessage = response.errors[0]?.message || 'Shift schedule creation failed';
          toast.error(errorMessage);
          console.error('GraphQL errors:', response.errors);
          return;
        }

        const createdTemplateId = response.data?.createShiftTemplate?.shiftTemplate?.id;

        if (createdTemplateId) {
          const courierIdsToAssign = formData.courierIds || [];

          for (const courierId of courierIdsToAssign) {
            try {
              await assignCourierToTemplateMutation({
                variables: {
                  input: {
                    shiftTemplateId: createdTemplateId,
                    courierId: courierId,
                  },
                },
              });
            } catch (error) {
              console.error(`Failed to assign courier ${courierId}:`, error);
            }
          }

          toast.success('Shift schedule created successfully');
          navigate('/shift-management', { state: { refresh: true, tab: 'templates' } });
        } else if (response.data?.createShiftTemplate) {
          toast.success('Shift schedule created successfully');
          navigate('/shift-management', { state: { refresh: true, tab: 'templates' } });
        } else {
          console.error('Unexpected response structure:', response);
          console.error('Response data:', response.data);
          console.error('Response errors:', response.errors);
          toast.error('Shift schedule creation failed. Please check console for details.');
        }
      }
    } catch (error: any) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} shift schedule`, error);
      const errorMessage = error.graphQLErrors?.[0]?.message || error.message || `Failed to ${isEditMode ? 'update' : 'create'} shift schedule. Please try again.`;
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/shift-management');
  };

  return (
    <div className="flex flex-col space-y-2 bg-white">
      <div className="main-wrapper">
        <div className="w-full px-4 sm:px-6 lg:px-8">

          <button
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/shift-management');
              }
            }}
            className="flex items-center gap-2 text-gray-600 hover:text-primaryColor transition-colors mb-6 group"
          >
            <ArrowBackIcon
              sx={{ fontSize: '20px' }}
              className="group-hover:translate-x-[-2px] transition-transform"
            />
            <span className="text-sm font-medium">Back</span>
          </button>


          <div className="mb-8">
            <div className="bg-gradient-to-r from-primaryColor via-primaryColor to-darkBlue rounded-2xl p-8 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24"></div>
              <div className="relative z-10">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-2xl border border-white/30">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 5V19M5 12H19" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h1 className="text-3xl font-bold text-white">{isEditMode ? 'Edit Shift Schedule' : 'Create New Shift Schedule'}</h1>
                      <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-semibold text-white border border-white/30">
                        {isEditMode ? 'Edit' : 'New'}
                      </span>
                    </div>
                    <p className="text-base text-white/90 leading-relaxed max-w-2xl">
                      {isEditMode
                        ? 'Update the shift schedule configuration. Changes will apply to newly generated shift sessions.'
                        : 'Create a recurring shift schedule for automatic shift session generation. Shift schedules can be daily, weekly, specific dates, or custom date ranges.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {isEditMode && templateLoading ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 lg:p-8">
              <div className="max-w-6xl mx-auto text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primaryColor mb-4"></div>
                <p className="text-gray-600">Loading template details...</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6 max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Shift Schedule Name <span className="text-red-500">*</span>
                      <Tooltip title="Name must be between 3 and 255 characters" arrow placement="top">
                        <InfoIcon sx={{ fontSize: '14px', color: '#9CA3AF', marginLeft: '4px', cursor: 'help' }} />
                      </Tooltip>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= 255) {
                          handleInputChange('name', value);
                        }
                      }}
                      placeholder="Enter shift schedule name (e.g., Morning Shift - Riyadh)"
                      required
                      minLength={3}
                      maxLength={255}
                      className={`w-full px-4 py-2.5 border rounded-md text-sm focus:ring-2 focus:ring-primaryColor focus:border-primaryColor outline-none transition-colors ${fieldErrors.name ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    <div className="flex items-center justify-between mt-1">
                      {fieldErrors.name ? (
                        <p className="text-xs text-red-600">{fieldErrors.name}</p>
                      ) : (
                        <p className="text-xs text-gray-500">Min: 3 characters, Max: 255 characters</p>
                      )}
                      <p className={`text-xs ${formData.name.length > 255 ? 'text-red-600' : formData.name.length > 230 ? 'text-amber-600' : 'text-gray-500'}`}>
                        {formData.name.length}/255
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Time <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="time"
                        value={formData.startTime}
                        onChange={(e) => handleInputChange('startTime', e.target.value)}
                        required
                        className={`w-full px-4 py-2.5 border rounded-md text-sm focus:ring-2 focus:ring-primaryColor focus:border-primaryColor outline-none transition-colors ${fieldErrors.startTime ? 'border-red-500' : 'border-gray-300'
                          }`}
                      />
                      {fieldErrors.startTime && (
                        <p className="mt-1 text-xs text-red-600">{fieldErrors.startTime}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Time <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="time"
                        value={formData.endTime}
                        onChange={(e) => handleInputChange('endTime', e.target.value)}
                        required
                        className={`w-full px-4 py-2.5 border rounded-md text-sm focus:ring-2 focus:ring-primaryColor focus:border-primaryColor outline-none transition-colors ${fieldErrors.endTime ? 'border-red-500' : 'border-gray-300'
                          }`}
                      />
                      {fieldErrors.endTime && (
                        <p className="mt-1 text-xs text-red-600">{fieldErrors.endTime}</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Required Drivers <span className="text-red-500">*</span>
                      <Tooltip title="Minimum number of drivers required for this template. Range: 1-100,000 drivers (1 lakh)" arrow placement="top">
                        <InfoIcon sx={{ fontSize: '14px', color: '#9CA3AF', marginLeft: '4px', cursor: 'help' }} />
                      </Tooltip>
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100000"
                      required
                      value={formData.requiredDrivers}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        if (value >= 1 && value <= 100000) {
                          handleInputChange('requiredDrivers', value);
                        } else if (value > 100000) {
                          handleInputChange('requiredDrivers', 100000);
                        } else if (value < 1 && e.target.value !== '') {
                          handleInputChange('requiredDrivers', 1);
                        }
                      }}
                      className={`w-full px-4 py-2.5 border rounded-md text-sm focus:ring-2 focus:ring-primaryColor focus:border-primaryColor outline-none transition-colors ${fieldErrors.requiredDrivers ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    <div className="flex items-center justify-between mt-1">
                      {fieldErrors.requiredDrivers ? (
                        <p className="text-xs text-red-600">{fieldErrors.requiredDrivers}</p>
                      ) : (
                        <p className="text-xs text-gray-500">Min: 1 driver, Max: 100,000 drivers</p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        City <span className="text-red-500">*</span>
                        <Tooltip title="Select a city for this shift schedule" arrow placement="top">
                          <InfoIcon sx={{ fontSize: '14px', color: '#9CA3AF', marginLeft: '4px', cursor: 'help' }} />
                        </Tooltip>
                      </label>
                      <Autocomplete
                        options={cityOptions}
                        getOptionLabel={(option) => option.name || ''}
                        value={cityOptions.find(city => String(city.id) === String(formData.cityId)) || null}
                        onChange={(event, newValue) => {
                          const value = newValue ? String(newValue.id) : '';
                          handleInputChange('cityId', value || undefined);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search and select a city"
                            required
                            error={!!fieldErrors.cityId}
                            helperText={fieldErrors.cityId}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                padding: '2px 4px',
                                fontSize: '14px',
                                '& fieldset': {
                                  borderColor: fieldErrors.cityId ? '#EF4444' : '#D1D5DB',
                                },
                                '&:hover fieldset': {
                                  borderColor: fieldErrors.cityId ? '#EF4444' : '#D1D5DB',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: fieldErrors.cityId ? '#EF4444' : '#303F9F',
                                  borderWidth: '2px',
                                },
                              },
                              '& .MuiInputBase-input': {
                                padding: '8px 12px',
                              },
                              '& .MuiFormHelperText-root': {
                                marginLeft: '0',
                              },
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} className="px-4 py-2 hover:bg-gray-100 cursor-pointer">
                            {option.name}
                          </li>
                        )}
                        sx={{
                          '& .MuiAutocomplete-inputRoot': {
                            padding: '0 !important',
                          },
                        }}
                        ListboxProps={{
                          style: {
                            maxHeight: '300px',
                          },
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Neighborhood / Working Zone
                        <Tooltip title="Optional: Select a specific city/neighborhood" arrow placement="top">
                          <InfoIcon sx={{ fontSize: '14px', color: '#9CA3AF', marginLeft: '4px', cursor: 'help' }} />
                        </Tooltip>
                      </label>
                      <Autocomplete
                        options={cityOptions}
                        getOptionLabel={(option) => option.name || ''}
                        value={cityOptions.find(city => String(city.id) === String(formData.neighborhoodId)) || null}
                        onChange={(event, newValue) => {
                          const value = newValue ? String(newValue.id) : '';
                          handleInputChange('neighborhoodId', value || undefined);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search and select a city/neighborhood (optional)"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                padding: '2px 4px',
                                fontSize: '14px',
                                '& fieldset': {
                                  borderColor: '#D1D5DB',
                                },
                                '&:hover fieldset': {
                                  borderColor: '#D1D5DB',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#303F9F',
                                  borderWidth: '2px',
                                },
                              },
                              '& .MuiInputBase-input': {
                                padding: '8px 12px',
                              },
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} className="px-4 py-2 hover:bg-gray-100 cursor-pointer">
                            {option.name}
                          </li>
                        )}
                        sx={{
                          '& .MuiAutocomplete-inputRoot': {
                            padding: '0 !important',
                          },
                        }}
                        ListboxProps={{
                          style: {
                            maxHeight: '300px',
                          },
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Recurrence Type <span className="text-red-500">*</span>
                    </label>
                    <Autocomplete
                      options={[
                        { value: 'daily', label: 'Daily' },
                        { value: 'weekly', label: 'Weekly' },
                        { value: 'specific_date', label: 'Specific Date' },
                        { value: 'custom_range', label: 'Custom Date Range' },
                      ]}
                      getOptionLabel={(option) => option.label || ''}
                      value={formData.recurrenceType ? { value: formData.recurrenceType, label: formData.recurrenceType === 'daily' ? 'Daily' : formData.recurrenceType === 'weekly' ? 'Weekly' : formData.recurrenceType === 'specific_date' ? 'Specific Date' : 'Custom Date Range' } : null}
                      onChange={(event, newValue) => {
                        const newRecurrenceType = newValue ? (newValue.value as RecurrenceType) : undefined;
                        handleInputChange('recurrenceType', newRecurrenceType);
                        setFieldErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.specificDate;
                          delete newErrors.validFrom;
                          delete newErrors.validUntil;
                          delete newErrors.daysOfWeek;
                          return newErrors;
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select recurrence type"
                          required
                          error={!!fieldErrors.recurrenceType}
                          helperText={fieldErrors.recurrenceType}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '0.875rem',
                              '& fieldset': {
                                borderWidth: '1px',
                                borderColor: fieldErrors.recurrenceType ? '#EF4444' : '#D1D5DB',
                              },
                              '&:hover fieldset': {
                                borderColor: fieldErrors.recurrenceType ? '#EF4444' : '#9CA3AF',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: fieldErrors.recurrenceType ? '#EF4444' : '#3B82F6',
                                borderWidth: '1px',
                              },
                            },
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.value} className="px-4 py-2 hover:bg-gray-50">
                          <span className="text-sm">{option.label}</span>
                        </li>
                      )}
                      noOptionsText="No recurrence type found"
                    />
                  </div>
                  {formData.recurrenceType === 'weekly' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Days of Week <span className="text-red-500">*</span>
                      </label>
                      {fieldErrors.daysOfWeek && (
                        <p className="mb-2 text-xs text-red-600">{fieldErrors.daysOfWeek}</p>
                      )}
                      <div className="grid grid-cols-4 gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        {DAYS_OF_WEEK.map((day) => (
                          <FormControlLabel
                            key={day.value}
                            control={
                              <Checkbox
                                checked={formData.daysOfWeek?.includes(day.value) || false}
                                onChange={() => handleDayToggle(day.value)}
                                size="small"
                              />
                            }
                            label={day.label}
                            className="text-sm"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {formData.recurrenceType === 'specific_date' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Specific Date <span className="text-red-500">*</span>
                      </label>
                      <DatePickerDDMMYY
                        value={formData.specificDate || ''}
                        onChange={(value) => handleInputChange('specificDate', value)}
                        placeholder="Specific Date: dd/mm/yy"
                        tooltip="Select a specific date for this shift schedule. Click to open calendar picker."
                        required
                        fullWidth
                        size="small"
                        error={!!fieldErrors.specificDate}
                        helperText={fieldErrors.specificDate}
                      />
                      {fieldErrors.specificDate && (
                        <p className="mt-1 text-xs text-red-600">{fieldErrors.specificDate}</p>
                      )}
                    </div>
                  )}
                  {formData.recurrenceType === 'custom_range' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valid From <span className="text-red-500">*</span>
                        </label>
                        <DatePickerDDMMYY
                          value={formData.validFrom || ''}
                          onChange={(value) => handleInputChange('validFrom', value)}
                          placeholder="Valid From: dd/mm/yy"
                          tooltip="Select the start date when this schedule becomes valid. Click to open calendar picker."
                          required
                          fullWidth
                          size="small"
                          error={!!fieldErrors.validFrom}
                          helperText={fieldErrors.validFrom}
                        />
                        {fieldErrors.validFrom && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors.validFrom}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valid Until <span className="text-red-500">*</span>
                        </label>
                        <DatePickerDDMMYY
                          value={formData.validUntil || ''}
                          onChange={(value) => handleInputChange('validUntil', value)}
                          placeholder="Valid Until: dd/mm/yy"
                          tooltip="Select the end date when this schedule expires. Click to open calendar picker. Must be after valid from date."
                          minDate={formData.validFrom}
                          required
                          fullWidth
                          size="small"
                          error={!!fieldErrors.validUntil}
                          helperText={fieldErrors.validUntil}
                        />
                        {fieldErrors.validUntil && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors.validUntil}</p>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 hidden">
                    <div className="flex items-center gap-2 mb-6">
                      <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#8B5CF6" />
                        </svg>
                      </div>
                      <h2 className="text-lg font-semibold text-gray-900">Advanced Settings</h2>
                    </div>

                    <div className="space-y-5">

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Priority
                          <Tooltip title="Higher priority templates take precedence when multiple templates apply to the same date" arrow>
                            <InfoIcon sx={{ fontSize: '14px', color: '#9CA3AF', marginLeft: '4px', cursor: 'help' }} />
                          </Tooltip>
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={formData.priority || ''}
                          onChange={(e) => handleInputChange('priority', e.target.value ? parseInt(e.target.value) : undefined)}
                          placeholder="Auto (leave empty for default)"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primaryColor focus:border-primaryColor outline-none transition-colors"
                        />
                      </div>


                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Generate Days Ahead
                          <Tooltip title="Number of days in advance to generate shifts from this template" arrow>
                            <InfoIcon sx={{ fontSize: '14px', color: '#9CA3AF', marginLeft: '4px', cursor: 'help' }} />
                          </Tooltip>
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="30"
                          value={formData.generateDaysAhead || 0}
                          onChange={(e) => handleInputChange('generateDaysAhead', parseInt(e.target.value) || 0)}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primaryColor focus:border-primaryColor outline-none transition-colors"
                        />
                      </div>


                      {formData.recurrenceType !== 'specific_date' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Exclude Dates
                            <Tooltip title="Dates to skip when generating shifts (e.g., holidays, special events)" arrow>
                              <InfoIcon sx={{ fontSize: '14px', color: '#9CA3AF', marginLeft: '4px', cursor: 'help' }} />
                            </Tooltip>
                          </label>
                          <div className="space-y-3">
                            <div className="flex gap-2">
                              <input
                                type="date"
                                value={newExcludeDate}
                                onChange={(e) => setNewExcludeDate(e.target.value)}
                                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primaryColor focus:border-primaryColor outline-none transition-colors"
                                placeholder="Select date to exclude"
                              />
                              <CustomButton
                                variant="outlined"
                                color="primary"
                                onClick={handleAddExcludeDate}
                                disabled={!newExcludeDate}
                                additionalStyles="px-4 py-2.5"
                              >
                                Add
                              </CustomButton>
                            </div>
                            {formData.excludeDates && formData.excludeDates.length > 0 && (
                              <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                {formData.excludeDates.map((date) => (
                                  <Chip
                                    key={date}
                                    label={new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                    onDelete={() => handleRemoveExcludeDate(date)}
                                    deleteIcon={<DeleteIcon />}
                                    size="small"
                                    className="bg-white border border-gray-300"
                                    sx={{
                                      '& .MuiChip-deleteIcon': {
                                        fontSize: '16px',
                                      },
                                    }}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <PreAssignedDriversSelector
                    selectedDriverIds={formData.courierIds || []}
                    onDriverIdsChange={(driverIds) => handleInputChange('courierIds', driverIds)}
                  />
                </div>
                <div className="lg:col-span-1 space-y-6">
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 sticky top-6">
                    <div className="flex items-center gap-2 mb-6">
                      <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#6366F1" />
                        </svg>
                      </div>
                      <h2 className="text-lg font-semibold text-gray-900">Status</h2>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Shift Schedule Status <span className="text-red-500">*</span>
                      </label>
                      <Autocomplete
                        options={[
                          { value: 'active', label: 'Active' },
                          { value: 'inactive', label: 'Inactive' },
                        ]}
                        getOptionLabel={(option) => option.label || ''}
                        value={formData.status ? { value: formData.status, label: formData.status === 'active' ? 'Active' : 'Inactive' } : null}
                        onChange={(event, newValue) => {
                          handleInputChange('status', newValue ? (newValue.value as TemplateStatus) : undefined);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select status"
                            required
                            error={!!fieldErrors.status}
                            helperText={fieldErrors.status}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                fontSize: '0.875rem',
                                borderRadius: '8px',
                                '& fieldset': {
                                  borderWidth: '1px',
                                  borderColor: fieldErrors.status ? '#EF4444' : '#D1D5DB',
                                },
                                '&:hover fieldset': {
                                  borderColor: fieldErrors.status ? '#EF4444' : '#9CA3AF',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: fieldErrors.status ? '#EF4444' : '#3B82F6',
                                  borderWidth: '1px',
                                },
                              },
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.value} className="px-4 py-2 hover:bg-gray-50">
                            <span className="text-sm">{option.label}</span>
                          </li>
                        )}
                        noOptionsText="No status found"
                      />
                    </div>
                    <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
                      <CustomButton
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={saving}
                        additionalStyles="w-full px-6 py-2.5"
                      >
                        {saving ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Shift Schedule' : 'Create Shift Schedule')}
                      </CustomButton>
                      <CustomButton
                        variant="outlined"
                        color="secondary"
                        onClick={handleCancel}
                        additionalStyles="w-full px-6 py-2.5"
                      >
                        Cancel
                      </CustomButton>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateShiftTemplate;

